import React, { useEffect, useState } from 'react'
import { Card, CardTitle, Bar } from '../UI'
import { ANALYSIS_DONE_STATUSES, LLM_PLATFORMS } from './constants'
import styles from '../../_css/Simulation.module.css'
import NextButton from './NextButton'
import Empty from './Empty'
import { getCrossPlatformAnalysis } from '../../requests'
import { Play } from 'lucide-react'

const safeObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {})
const safeArray = (v) => (Array.isArray(v) ? v : [])
const safeNum = (v, def = 0) => (typeof v === 'number' && !Number.isNaN(v) ? v : def)

const topBrandOf = (sov) => {
  const entries = Object.entries(safeObject(sov))
  if (!entries.length) return null
  return entries.sort((a, b) => safeNum(b[1]) - safeNum(a[1]))[0][0]
}

// Brand names come from the pipeline's output (arbitrary client/competitor brands),
// so colors are assigned deterministically from a fixed palette rather than hardcoded.
const BRAND_PALETTE = [
  '#0a3d91', '#00a1de', '#f5a800', '#6c4bd8', '#c8008f',
  '#22c55e', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6',
]
function colorForBrand(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  return BRAND_PALETTE[hash % BRAND_PALETTE.length]
}

function CrossPlatformTab({
  recentSimulationId = null,
  recentSimulationStatus = null,
  recentSimulationDetails = null,
  selectedRun = null,
  onReturnToLatestRun = () => {},
  onGoToRuns = null,
}) {
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(recentSimulationStatus)
  const [crossPlatform, setCrossPlatform] = useState(null)
  const [sort, setSort] = useState({ col: null, dir: 'desc' })

  useEffect(() => {
    // A specific historical run was explicitly picked in the Runs tab — show it regardless of
    // the simulation's live status, instead of falling through to the "always latest" fetch below.
    if (selectedRun) {
      setCrossPlatform(selectedRun?.results?.cross_platform ?? null)
      setStatus(6)
      setLoading(false)
      return
    }

    // Fast path: the parent may already have fetched this run's `results` (getAiSimulationDetails
    // already selects it), so reuse it instead of firing a second request.
    const embeddedRun = recentSimulationDetails?.aiSimulationRuns?.[0]
    const embeddedCrossPlatform = embeddedRun?.results?.cross_platform
    if (
      embeddedCrossPlatform &&
      (recentSimulationStatus === 6 || recentSimulationStatus === 91)
    ) {
      setCrossPlatform(embeddedCrossPlatform)
      setStatus(recentSimulationStatus)
      setLoading(false)
      return
    }
    if (!recentSimulationId) {
      setCrossPlatform(null)
      setLoading(false)
      return
    }
    setLoading(true)
    getCrossPlatformAnalysis(recentSimulationId)
      .then((res) => {
        setCrossPlatform(res?.data?.cross_platform ?? null)
        setStatus(res?.data?.status ?? recentSimulationStatus)
      })
      .catch(() => setCrossPlatform(null))
      .finally(() => setLoading(false))
  }, [recentSimulationId, recentSimulationStatus, recentSimulationDetails, selectedRun])

  if (!recentSimulationId) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    )
  }
  if (loading) {
    return <Empty message="Loading cross-platform analysis..." type="progress" />
  }
  if (status === -1) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    )
  }
  if (status === 1 || status === 2 || status === 3) {
    return (
      <Empty message="Queries generation in progress..." type="progress" />
    )
  }
  if (status === 4) {
    return (
      <Empty
        message="Queries generation completed. Please go to Queries tab and select the queries you want to run. Then click on the Run Simulation button to start the simulation."
        type="progress"
      />
    )
  }
  // A new run may be processing (status 5/90) or in some other transient state, but if an
  // earlier run already completed, keep showing its data instead of blocking the whole tab.
  const hasCompletedData = crossPlatform != null

  if ((status === 5 || status === 90) && !hasCompletedData) {
    return (
      <Empty message="Cross-platform analysis in progress..." type="progress" />
    )
  }
  if (!ANALYSIS_DONE_STATUSES.includes(Number(status)) && !hasCompletedData) {
    return (
      <Empty
        message="AI is still analyzing the data. Please wait for a moment."
        type="progress"
      />
    )
  }

  const cp = safeObject(crossPlatform)
  const platformsObj = safeObject(cp.platforms)
  const CLIENT = cp.client_brand || 'Client'
  const items = LLM_PLATFORMS.filter((p) => platformsObj[p.id]).map((p) => ({
    ...p,
    r: platformsObj[p.id],
  }))

  if (items.length < 2) {
    return (
      <Empty message="Cross-Platform Analysis needs at least two LLM platforms with results for this simulation." />
    )
  }

  const brandSet = new Set(safeArray(cp.brands).filter(Boolean))
  items.forEach((x) =>
    Object.keys(safeObject(x.r.sov)).forEach((b) => brandSet.add(b)),
  )
  const brands = Array.from(brandSet)

  const clientRanked = [...items].sort(
    (a, b) => safeNum(b.r.sov?.[CLIENT]) - safeNum(a.r.sov?.[CLIENT]),
  )
  const best = clientRanked[0]
  const worst = clientRanked[clientRanked.length - 1]
  const maxClient = safeNum(best?.r.sov?.[CLIENT])
  const leaders = items.map((x) => ({
    platform: x.label,
    brand: topBrandOf(x.r.sov),
  }))
  const clientLeadCount = leaders.filter((l) => l.brand === CLIENT).length
  const contested = leaders.filter((l) => l.brand && l.brand !== CLIENT)

  const handleSort = (col) => {
    setSort((cur) =>
      cur.col === col
        ? { col, dir: cur.dir === 'desc' ? 'asc' : 'desc' }
        : { col, dir: col === 'brand' ? 'asc' : 'desc' },
    )
  }
  const sortArrow = (col) =>
    sort.col === col ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''

  const sortedBrands = [...brands].sort((a, b) => {
    if (sort.col === 'brand') return sort.dir === 'desc' ? b.localeCompare(a) : a.localeCompare(b)
    if (sort.col) {
      const platform = items.find((x) => x.id === sort.col)
      if (platform) {
        const diff = safeNum(platform.r.sov?.[b]) - safeNum(platform.r.sov?.[a])
        return sort.dir === 'asc' ? -diff : diff
      }
    }
    return 0
  })

  return (
    <div className={styles.tabPage}>
      <div className={styles.tabHeaderRow}>
        <div>
          <h2 className={styles.personaProfileTitle}>Cross-Platform Analysis</h2>
          <p className={styles.personaProfileDesc}>
            Comparing {CLIENT} brand visibility across {items.length} LLM platforms:{' '}
            {items.map((x) => x.label).join(', ')}.
          </p>
        </div>
      </div>

      {selectedRun && (
        <div
          onClick={onReturnToLatestRun}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#b45309',
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 999, padding: '4px 10px', marginBottom: 12, cursor: 'pointer',
          }}
        >
          ← Viewing Run #{selectedRun.id} — click to return to latest
        </div>
      )}

      <div className={styles.crossPlatformKpiGrid}>
        {items.map((x) => (
          <Card key={x.id} style={{ padding: '16px 18px', borderTop: `3px solid ${x.color}` }}>
            <div className={styles.crossPlatformKpiHeader}>
              <span className={styles.crossPlatformKpiDot} style={{ background: x.color }} />
              <span className={styles.crossPlatformKpiPlatform}>{x.label}</span>
            </div>
            <div className={styles.crossPlatformKpiBody}>
              <div className={styles.crossPlatformKpiRow}>
                <span className={styles.crossPlatformKpiLabel}>Mention Rate</span>
                <span className={styles.crossPlatformKpiValue}>{safeNum(x.r.mentionRate)}%</span>
              </div>
              <div className={styles.crossPlatformKpiRow}>
                <span className={styles.crossPlatformKpiLabel}>Share of Voice</span>
                <span className={styles.crossPlatformKpiValue}>
                  {x.r.sovRank ?? '#—'} · {safeNum(x.r.sov?.[CLIENT])}%
                </span>
              </div>
              <div className={styles.crossPlatformKpiRow}>
                <span className={styles.crossPlatformKpiLabel}>Avg. Position</span>
                <span className={styles.crossPlatformKpiValue}>{x.r.avgPos ?? '—'}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <CardTitle sub={`${CLIENT} share of voice on each platform — higher is better`}>
          {CLIENT} Share of Voice by Platform
        </CardTitle>
        <div className={styles.compSovList}>
          {[...items]
            .sort((a, b) => safeNum(b.r.sov?.[CLIENT]) - safeNum(a.r.sov?.[CLIENT]))
            .map((x) => (
              <div key={x.id} className={styles.compSovRow}>
                <span className={styles.compSovName} style={{ minWidth: 90 }}>
                  {x.label}
                </span>
                <div className={styles.compSovBar}>
                  <Bar
                    pct={safeNum(x.r.sov?.[CLIENT])}
                    color={x.color}
                    showLabel={false}
                    height={10}
                  />
                </div>
                <span className={styles.compSovPct}>{safeNum(x.r.sov?.[CLIENT])}%</span>
              </div>
            ))}
        </div>
      </Card>

      <Card>
        <CardTitle sub="Share of voice per brand across platforms — ▲ marks the platform leader · click a column header to sort">
          Share of Voice Matrix
        </CardTitle>
        <div className={styles.crossPlatformTableWrap}>
          <table className={styles.crossPlatformTable}>
            <thead>
              <tr>
                <th
                  className={`${styles.crossPlatformTh} ${styles.crossPlatformThBrand}`}
                  onClick={() => handleSort('brand')}
                >
                  Brand{sortArrow('brand')}
                </th>
                {items.map((x) => (
                  <th
                    key={x.id}
                    className={styles.crossPlatformTh}
                    onClick={() => handleSort(x.id)}
                  >
                    <span className={styles.crossPlatformThPlatform}>
                      <span className={styles.crossPlatformKpiDot} style={{ background: x.color }} />
                      {x.label}
                      {sortArrow(x.id)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedBrands.map((brand) => {
                const isClient = brand === CLIENT
                return (
                  <tr
                    key={brand}
                    className={isClient ? styles.crossPlatformRowClient : undefined}
                  >
                    <td className={styles.crossPlatformTdBrand}>
                      <span
                        className={styles.crossPlatformKpiDot}
                        style={{ background: colorForBrand(brand) }}
                      />
                      {brand}
                    </td>
                    {items.map((x) => {
                      const v = safeNum(x.r.sov?.[brand])
                      const leader = topBrandOf(x.r.sov) === brand
                      return (
                        <td
                          key={x.id}
                          className={styles.crossPlatformTd}
                          style={{ fontWeight: leader ? 700 : 400 }}
                        >
                          {v}%{leader && <span className={styles.crossPlatformLeaderMark}>▲</span>}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardTitle
          sub={`How each platform frames ${CLIENT} when it appears (positive / neutral / negative)`}
        >
          {CLIENT} Sentiment by Platform
        </CardTitle>
        <div className={styles.crossPlatformSentimentList}>
          {items.map((x) => {
            const s = safeObject(x.r.sentiment)
            const total =
              safeNum(s.positive) + safeNum(s.neutral) + safeNum(s.negative) || 1
            return (
              <div key={x.id} className={styles.crossPlatformSentimentRow}>
                <span className={styles.compSovName} style={{ minWidth: 90 }}>
                  {x.label}
                </span>
                <div className={styles.crossPlatformSentimentBar}>
                  <div
                    style={{ width: `${(safeNum(s.positive) / total) * 100}%`, background: '#22c55e' }}
                  />
                  <div
                    style={{ width: `${(safeNum(s.neutral) / total) * 100}%`, background: '#f59e0b' }}
                  />
                  <div
                    style={{ width: `${(safeNum(s.negative) / total) * 100}%`, background: '#ef4444' }}
                  />
                </div>
                <span className={styles.crossPlatformSentimentCounts}>
                  {safeNum(s.positive)} pos · {safeNum(s.neutral)} neu · {safeNum(s.negative)} neg
                </span>
              </div>
            )
          })}
        </div>
      </Card>

      <Card>
        <CardTitle>Cross-Platform Insights</CardTitle>
        <div className={styles.crossPlatformInsightsList}>
          {best && worst && (
            <div className={styles.crossPlatformInsightRow}>
              <span className={styles.crossPlatformInsightDot} style={{ background: '#22c55e' }} />
              <span className={styles.crossPlatformInsightText}>
                {CLIENT} is most visible on {best.label} ({safeNum(best.r.sov?.[CLIENT])}% SOV,{' '}
                {best.r.sovRank ?? '#—'}) and least visible on {worst.label} (
                {safeNum(worst.r.sov?.[CLIENT])}%).
              </span>
            </div>
          )}
          <div className={styles.crossPlatformInsightRow}>
            <span className={styles.crossPlatformInsightDot} style={{ background: '#3b82f6' }} />
            <span className={styles.crossPlatformInsightText}>
              {CLIENT} holds the #1 share of voice on {clientLeadCount} of {items.length}{' '}
              platforms.
            </span>
          </div>
          {contested.map((l) => (
            <div key={l.platform} className={styles.crossPlatformInsightRow}>
              <span className={styles.crossPlatformInsightDot} style={{ background: '#ef4444' }} />
              <span className={styles.crossPlatformInsightText}>
                On {l.platform}, {l.brand} outranks {CLIENT} — prioritise {l.platform}-specific
                content and citations.
              </span>
            </div>
          ))}
          {best && worst && (
            <div className={styles.crossPlatformInsightRow}>
              <span className={styles.crossPlatformInsightDot} style={{ background: '#f59e0b' }} />
              <span className={styles.crossPlatformInsightText}>
                Visibility spread of{' '}
                {Math.round((maxClient - safeNum(worst.r.sov?.[CLIENT])) * 10) / 10} pts across
                platforms — results are not interchangeable; track each platform separately.
              </span>
            </div>
          )}
        </div>
      </Card>

      <div className={styles.crossPlatformNotesGrid}>
        {items.map((x) => (
          <div
            key={x.id}
            className={styles.crossPlatformNoteCard}
            style={{ borderLeft: `4px solid ${x.color}` }}
          >
            <div className={styles.crossPlatformNoteTitle}>
              {x.label} <span className={styles.crossPlatformNoteProvider}>· {x.provider}</span>
            </div>
            <div className={styles.crossPlatformNoteText}>{x.r.note || 'Not available'}</div>
          </div>
        ))}
      </div>

      {typeof onGoToRuns === 'function' && (
        <div className={styles.personaActions}>
          <NextButton label="Continue to Runs" onClick={onGoToRuns} icon={<Play size={16} />} />
        </div>
      )}
    </div>
  )
}

export default CrossPlatformTab
